import { ChangeDetectionStrategy, Component, OnDestroy, computed, inject, signal } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { EMPTY, Subscription, Subject, catchError, distinctUntilChanged, expand, filter, forkJoin, map, merge, of, reduce, switchMap } from 'rxjs';
import { MonitoringBriefResponseDto } from '@/app/common/general-types/social-screening.dto';
import { SocialMonitoring } from '@/app/services/social-monitoring/social-monitoring';
import { SuperSocialService } from '@/app/services/super-social/super-social.service';
import { ScreeningTabDef, ScreeningTabs } from '../manage-brief-tabs-screen/ui/screening-tabs/screening-tabs';
import { ManageBriefTabsMonitorService } from './services/manage-brief-tabs-monitor/manage-brief-tabs-monitor';
import { MonBriefManagement } from './molecules/mon-brief-management/mon-brief-management';
import { MonContent } from './molecules/mon-content/mon-content';
import { MonCreators } from './molecules/mon-creators/mon-creators';
import { MonResults } from './molecules/mon-results/mon-results';
import { MonOverview } from './molecules/mon-overview/mon-overview';
import { InquiryRow, MonitorCreator, briefCreators } from './molecules/mon-shell/brief-creators';
import { POSTS_FOUND_PAGE, countFromPages } from './molecules/mon-content/content-status';
import { PublishedPostLink, ResultsStat, joinResults, unfilteredViews } from './molecules/mon-results/results-scan';
import {
  MonitorBriefFacts,
  MonitorTab,
  daysLeftLabel,
  isMonitorTab,
  monitorTabs,
  parseMonitorTab,
  statusLabel,
} from './molecules/mon-shell/monitor-shell';

const CREATOR_COUNT_PAGE = 100;

@Component({
  selector: 'app-manage-brief-tabs-monitor',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ScreeningTabs, MonOverview, MonBriefManagement, MonContent, MonCreators, MonResults],
  templateUrl: './manage-brief-tabs-monitor.html',
  styleUrl: './manage-brief-tabs-monitor.css',
})
export class ManageBriefTabsMonitor implements OnDestroy {
  private readonly subscriptionList = new Subscription();
  private readonly requestedBriefId = new Subject<string>();
  private readonly refreshCreatorCount = new Subject<void>();

  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly socialMonitoringService = inject(SocialMonitoring);
  private readonly superSocialService = inject(SuperSocialService);
  readonly manageBriefTabsMonitorService = inject(ManageBriefTabsMonitorService);

  readonly briefId = signal<string | undefined>(undefined);
  readonly postsFound = signal<number | null>(null);
  readonly creatorCount = signal<number | null>(null);
  readonly creators = signal<MonitorCreator[] | null>(null);
  readonly resultStats = signal<ResultsStat[] | null>(null);
  readonly openCreatorPicker = signal(false);
  readonly selectedTab = signal<MonitorTab>('overview');
  readonly loadingState = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');

  readonly brief = computed(
    () => this.manageBriefTabsMonitorService.focussedMonitoringBrief() as MonitoringBriefResponseDto | undefined,
  );

  readonly daysLeft = computed(() =>
    daysLeftLabel((this.brief() as MonitorBriefFacts | undefined)?.monitorUntil, new Date()),
  );

  readonly campaignStart = computed(() => (this.brief() as MonitorBriefFacts | undefined)?.monitorStart ?? null);

  readonly statusText = computed(() =>
    statusLabel((this.brief() as MonitorBriefFacts | undefined)?.currentMonitoringKanbanProgress?.status),
  );

  readonly postsFoundLabel = computed(() => {
    const count = this.postsFound();
    return count === null ? '—' : String(count);
  });

  readonly creatorCountLabel = computed(() => {
    const count = this.creatorCount();
    return count === null ? '—' : String(count);
  });

  readonly viewsLabel = computed(() => {
    const rows = this.resultStats();
    return rows === null ? '—' : String(unfilteredViews(rows));
  });

  readonly tabs = computed((): ScreeningTabDef[] => {
    const results = this.resultStats();
    return monitorTabs({
      postsFound: this.postsFound(),
      creatorCount: this.creatorCount(),
      resultsCount: results === null ? null : results.length,
    });
  });

  constructor() {
    this.subscriptionList.add(
      this.requestedBriefId
        .pipe(
          distinctUntilChanged(),
          switchMap((briefId) => {
            this.loadingState.set('loading');
            return this.socialMonitoringService.getMonitoringBriefById(briefId).pipe(
              map((brief) => ({ brief, failed: false as const })),
              catchError(() => of({ brief: null, failed: true as const })),
            );
          }),
        )
        .subscribe((result) => {
          if (result.failed || !result.brief) {
            this.manageBriefTabsMonitorService.focussedMonitoringBrief.set(undefined);
            this.loadingState.set('error');
            return;
          }
          this.manageBriefTabsMonitorService.focussedMonitoringBrief.set(result.brief);
          this.loadingState.set('loaded');
        }),
    );

    this.subscriptionList.add(
      this.requestedBriefId
        .pipe(
          distinctUntilChanged(),
          switchMap((briefId) => {
            this.postsFound.set(null);
            return this.publishedPostCount(briefId).pipe(catchError(() => of(null)));
          }),
        )
        .subscribe((total) => this.postsFound.set(total)),
    );

    this.subscriptionList.add(
      merge(
        this.requestedBriefId.pipe(
          distinctUntilChanged(),
          map((briefId) => ({ briefId, reset: true })),
        ),
        this.refreshCreatorCount.pipe(
          map(() => this.briefId()),
          filter((briefId): briefId is string => !!briefId),
          map((briefId) => ({ briefId, reset: false })),
        ),
      )
        .pipe(
          switchMap(({ briefId, reset }) => {
            if (reset) {
              this.creatorCount.set(null);
              this.creators.set(null);
            }
            return this.briefInquiries(briefId).pipe(catchError(() => of(null)));
          }),
        )
        .subscribe((inquiries) => {
          this.creatorCount.set(inquiries === null ? null : inquiries.length);
          this.creators.set(inquiries === null ? null : briefCreators(inquiries));
        }),
    );

    this.subscriptionList.add(
      this.requestedBriefId
        .pipe(
          distinctUntilChanged(),
          switchMap((briefId) => {
            this.resultStats.set(null);
            return this.resultsSnapshot(briefId).pipe(catchError(() => of([] as ResultsStat[])));
          }),
        )
        .subscribe((rows) => this.resultStats.set(rows)),
    );

    this.subscriptionList.add(
      this.route.paramMap.subscribe((params) => this.applyParams(params)),
    );
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
    this.manageBriefTabsMonitorService.focussedMonitoringBrief.set(undefined);
    this.manageBriefTabsMonitorService.selectedComplianceDocs.set(undefined);
  }

  requestAddCreators(): void {
    this.openCreatorPicker.set(true);
    this.selectTab('creators');
  }

  onCreatorPickerOpened(): void {
    this.openCreatorPicker.set(false);
  }

  onCreatorsChanged(): void {
    this.refreshCreatorCount.next();
  }

  selectTab(tab: string): void {
    if (!isMonitorTab(tab) || tab === this.selectedTab()) {
      return;
    }
    const briefId = this.briefId();
    this.selectedTab.set(tab);
    if (!briefId) {
      return;
    }
    void this.router.navigate(['/manage-brief-monitor', briefId, tab], {
      queryParams: {},
      replaceUrl: true,
    });
  }

  goBack(): void {
    this.location.back();
  }

  private publishedPostCount(briefId: string) {
    return this.socialMonitoringService
      .getPublishedContent({ briefId, take: POSTS_FOUND_PAGE, skip: 0, query: undefined })
      .pipe(
        expand((rows, index) => {
          const page = rows ?? [];
          if (countFromPages([page.length], POSTS_FOUND_PAGE) !== null) {
            return EMPTY;
          }
          return this.socialMonitoringService.getPublishedContent({
            briefId,
            take: POSTS_FOUND_PAGE,
            skip: (index + 1) * POSTS_FOUND_PAGE,
            query: undefined,
          });
        }),
        reduce((total, rows) => total + (rows?.length ?? 0), 0),
      );
  }

  private briefInquiries(briefId: string) {
    return this.socialMonitoringService.getInquiriesByBriefId(briefId, CREATOR_COUNT_PAGE, 0).pipe(
      expand((rows, index) => {
        const page = rows ?? [];
        if (countFromPages([page.length], CREATOR_COUNT_PAGE) !== null) {
          return EMPTY;
        }
        return this.socialMonitoringService.getInquiriesByBriefId(
          briefId,
          CREATOR_COUNT_PAGE,
          (index + 1) * CREATOR_COUNT_PAGE,
        );
      }),
      reduce((all, rows) => all.concat((rows ?? []) as InquiryRow[]), [] as InquiryRow[]),
    );
  }

  private resultsSnapshot(briefId: string) {
    return forkJoin({
      postIds: this.socialMonitoringService.getPublishedContentPostIds(briefId).pipe(catchError(() => of([] as string[]))),
      published: this.allPublished(briefId),
    }).pipe(
      switchMap(({ postIds, published }) => {
        const ids = (postIds ?? []).map((id) => id.trim()).filter((id) => id.length > 0);
        if (ids.length === 0) {
          return of(joinResults(published, {}));
        }
        return this.superSocialService.getCompleteLatestResultsForPostsFree(ids).pipe(
          map((stats) => joinResults(published, stats ?? {})),
        );
      }),
    );
  }

  private allPublished(briefId: string) {
    return this.socialMonitoringService
      .getPublishedContent({ briefId, take: POSTS_FOUND_PAGE, skip: 0, query: undefined })
      .pipe(
        expand((rows, index) => {
          const page = rows ?? [];
          if (countFromPages([page.length], POSTS_FOUND_PAGE) !== null) {
            return EMPTY;
          }
          return this.socialMonitoringService.getPublishedContent({
            briefId,
            take: POSTS_FOUND_PAGE,
            skip: (index + 1) * POSTS_FOUND_PAGE,
            query: undefined,
          });
        }),
        reduce((all, rows) => all.concat(rows ?? []), [] as PublishedPostLink[]),
      );
  }

  private applyParams(params: ParamMap): void {
    const briefId = params.get('briefId') ?? undefined;
    this.briefId.set(briefId);
    this.selectedTab.set(parseMonitorTab(params.get('tab')));
    if (briefId) {
      this.requestedBriefId.next(briefId);
    }
  }
}
